import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NgMapsViewComponent } from '@ng-maps/core';
import { NgMapsInfoWindowComponent } from '@ng-maps/core/lib/directives/info-window';
import { GoogleComponent } from '@ng-maps/google';
import { of, Subject } from 'rxjs';
import { catchError, startWith, switchMap, takeUntil } from 'rxjs/operators';
import ProjectMapHelper from 'src/app/helpers/project-map-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import Company from 'src/app/model/company/company';
import { Project } from 'src/app/model/project/project';
import { ClientCompanySelectService } from 'src/app/services/client-company-select.service';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';
import { DoOnce } from '../../../subscription-helpers/subscriptions';
import {
  ClientCornerMapHelper,
  ProjectGroup,
} from './helpers/client-corner-map-helper';
import { ClientCornerMapAddressSearchControllerRequestService } from './services/client-corner-map-address-search-controller-request.service';
import {
  ClientCornerMapControllerRequestService,
  ProjectClientMapFilterType,
} from './services/client-corner-map-controller-request.service';

@Component({
  selector: 'client-corner-map',
  templateUrl: './client-corner-map.component.html',
  styleUrls: ['./client-corner-map.component.scss'],
})
export class ClientCornerMapComponent
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  projectGroups: ProjectGroup[];

  private company: Company;

  INPRESS_COORDINATES = ProjectMapHelper.INPRESS_COORDINATES;
  HIDE_POI_STYLE = ProjectMapHelper.HIDE_POI_STYLE;
  PROJECT_CLIENT_MAP_FILTER_TYPE = ProjectClientMapFilterType;

  selectedFilterType = ProjectClientMapFilterType.ALL;
  addressLine1: string;
  addressPostalCode: string;

  init = false;
  currentIW: NgMapsInfoWindowComponent;
  previousIW: NgMapsInfoWindowComponent;

  @ViewChild(GoogleComponent, { static: true })
  agmMap: NgMapsViewComponent<any>;
  map: google.maps.Map;

  getProjectsSubject: Subject<any>;
  private initSubject = new Subject<boolean>();

  constructor(
    private clientCornerMapControllerRequestService: ClientCornerMapControllerRequestService,
    private clientCornerMapAddressSearchControllerRequestService: ClientCornerMapAddressSearchControllerRequestService,
    private clientCompanySelectService: ClientCompanySelectService
  ) {
    super();
  }

  ngOnInit(): void {
    this.getProjectsSubject = new Subject<any>();

    this.company = this.clientCompanySelectService.getSelectedCompany();

    this.addSubscription(
      this.getProjectsSubject.pipe(
        startWith([]),
        switchMap(async () => {
          this.setAndEmitInit(false);
          return this.company == null
            ? []
            : await this.getProjectsRequestPromise();
        }),
        catchError(() => {
          return of([]);
        }),
        takeUntil(this.destroyThisComponent)
      ),
      (res: Project[]) => {
        this.projectGroups = ClientCornerMapHelper.constructSameCoordsArray(
          res,
          this.selectedFilterType === ProjectClientMapFilterType.SOLD_RENTED
        );
        this.setAndEmitInit(true);
      }
    );
    this.addSubscription(
      this.clientCompanySelectService.getSelectionChangedSubscribable(),
      (res: Company) => {
        const previousCompany = this.company;
        this.company = res;
        if (
          previousCompany == null ||
          (this.company != null && previousCompany.id !== this.company.id)
        ) {
          this.getProjectsSubject.next();
        }
      }
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.getProjectsSubject != null) {
      this.getProjectsSubject.complete();
    }
    if (this.initSubject != null) {
      this.initSubject.complete();
    }
  }

  ngAfterViewInit(): void {
    DoOnce(this.agmMap.mapReady, (map: google.maps.Map) => {
      this.map = map;
      this.centerMapOnBelgium();
      this.addSubscription(this.initSubject, (b) => {
        if (!b) {
          if (this.projectGroups != null) {
            this.projectGroups.length = 0;
          }
          this.centerMapOnBelgium();
        }
      });
    });
  }

  private getProjectsRequestPromise(): Promise<Project[]> {
    return this.selectedFilterType != null
      ? this.clientCornerMapControllerRequestService.clientMapProjects(
          this.company.id,
          this.selectedFilterType
        )
      : this.clientCornerMapAddressSearchControllerRequestService.clientMapProjectsForAddress(
          this.company.id,
          this.addressLine1,
          this.addressPostalCode
        );
  }

  private centerMapOnBelgium(): void {
    this.map.setZoom(8);
    this.map.panTo(ProjectMapHelper.BELGIUM_GEOGRAPHICAL_CENTER);
  }

  private setAndEmitInit(value: boolean) {
    this.init = value;
    if (!value) {
      if (this.currentIW != null) {
        this.currentIW.close();
        this.currentIW = null;
      }
      if (this.previousIW != null) {
        this.previousIW.close();
        this.previousIW = null;
      }
    }
    this.initSubject.next(value);
  }

  mapClick(): void {
    if (this.previousIW) {
      this.previousIW.close();
    }
  }

  markerClick(infoWindow: NgMapsInfoWindowComponent): void {
    if (this.previousIW) {
      this.currentIW = infoWindow;
      this.previousIW.close();
    }
    this.previousIW = infoWindow;
  }

  selectedFilterTypeChanged(filterType: ProjectClientMapFilterType): void {
    this.selectedFilterType = filterType;
    this.getProjectsSubject.next();
  }

  searchOnAddress(): void {
    if (
      StringHelper.isEmpty(this.addressLine1) &&
      StringHelper.isEmpty(this.addressPostalCode)
    ) {
      return;
    }
    this.selectedFilterType = null;
    this.getProjectsSubject.next();
  }
}
